import React, { useEffect, useState } from "react";
import {
  format,
  setMonth,
  setYear,
  subMonths,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isSameDay,
} from "date-fns";
import dayjs from "dayjs";
import { ChevronLeft, ChevronRight } from "lucide-react";

const CalendarHomeTest = ({ sendSelectedDateToEvents }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const monthNames = [
    "JAN", "FEB", "MAR", "APR", "MAY", "JUN",
    "JUL", "AUG", "SEP", "OCT", "NOV", "DEC",
  ];

  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - 5 + i,
  );

  const handleMonthClick = (monthIndex) => {
    setCurrentDate(setMonth(currentDate, monthIndex));
  };

  const handleYearChange = (e) => {
    setCurrentDate(setYear(currentDate, parseInt(e.target.value)));
  };

  const goToPrevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToNextMonth = () => setCurrentDate(addMonths(currentDate, 1));

  const today = new Date();

  const generateCalendarDays = () => {
    const startMonth = startOfMonth(currentDate);
    const endMonth = endOfMonth(currentDate);
    const startDate = startOfWeek(startMonth, { weekStartsOn: 1 });
    const endDate = endOfWeek(endMonth, { weekStartsOn: 1 });

    const days = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days.map((date, index) => {
      const isCurrentMonth = isSameMonth(date, currentDate);
      const isSelected = isSameDay(date, selectedDate);
      const isToday = isSameDay(date, today);

      return (
        <div
          key={index}
          onClick={() => setSelectedDate(date)}
          className={`
            relative flex items-center justify-center
            h-8 w-8 mx-auto rounded-xl
            text-xs font-medium cursor-pointer
            transition-all duration-150 select-none
            ${!isCurrentMonth ? "text-slate-300 cursor-default" : ""}
            ${isCurrentMonth && !isSelected && !isToday
              ? "text-slate-700 hover:bg-blue-50 hover:text-blue-600"
              : ""}
            ${isToday && !isSelected
              ? "text-blue-600 font-semibold ring-2 ring-blue-200 ring-offset-1"
              : ""}
            ${isSelected
              ? "bg-blue-600 text-white shadow-sm shadow-blue-200 font-semibold"
              : ""}
          `}
        >
          {format(date, "d")}
        </div>
      );
    });
  };

  useEffect(() => {
    console.log("selectedDate:", dayjs(selectedDate).format("YYYY-MM-DD"));
    const selectedDateFormatted = dayjs(selectedDate).format("YYYY-MM-DD");
    sendSelectedDateToEvents(selectedDateFormatted);
  }, [selectedDate]);

  const currentMonthLabel = format(currentDate, "MMMM yyyy");

  return (
    <div className="w-full h-full bg-white rounded-2xl  shadow-md overflow-hidden select-none">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between p-3 border-b border-slate-100">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">
            Calendar
          </p>
          <h2 className="text-base font-bold text-slate-900 leading-tight mt-0.5">
            {format(currentDate, "MMMM")}{" "}<span className="ml-2.5 pl-4 font-normal text-slate-400">
              {format(currentDate, "yyyy")}
            </span>
            
          </h2>
        </div>

        <div className="flex items-center gap-1">
          {/* Year dropdown */}
          <select
            className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs font-medium text-slate-600 outline-none transition hover:border-slate-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 cursor-pointer"
            value={format(currentDate, "yyyy")}
            onChange={handleYearChange}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          {/* Prev / Next */}
          <button
            onClick={goToPrevMonth}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <ChevronLeft size={15} />
          </button>
          <button
            onClick={goToNextMonth}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
          >
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* ── Month Pill Selector ─────────────────────────────────── */}
      <div className="grid grid-cols-6 gap-1 px-4 py-3 border-b border-slate-100">
        {monthNames.map((m, idx) => {
          const isActive = idx === currentDate.getMonth();
          return (
            <button
              key={idx}
              onClick={() => handleMonthClick(idx)}
              className={`rounded-lg py-1 text-[10px] font-semibold tracking-wide transition-all ${
                isActive
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              }`}
            >
              {m}
            </button>
          );
        })}
      </div>

      {/* ── Day Headers ────────────────────────────────────────── */}
      <div className="grid grid-cols-7 px-3 pt-3 pb-1">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div
            key={d}
            className="text-center text-[10px] font-semibold uppercase tracking-wide text-slate-400"
          >
            {d}
          </div>
        ))}
      </div>

      {/* ── Days Grid ──────────────────────────────────────────── */}
      <div className="grid grid-cols-7 gap-y-1 px-3 pb-3 ">
        {generateCalendarDays()}
      </div>
    </div>
  );
};

export default CalendarHomeTest;